import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro-6";
import { AdapterDayjs } from "@mui/x-date-pickers-pro-6/AdapterDayjs";
import dayjs from "dayjs";
import { Field, Form, Formik, FormikHelpers } from "formik";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import LoadingButton from "../../../../../../design/wilya-loading-button";
import { useDayjsLocale } from "../../../../../../utils/date-locale";
import {
	Holiday,
	HolidayFormValues,
	HolidayScope,
	HolidayType,
} from "../../../../../holidays/types";
import { toDateKey } from "../../../../../holidays/utils/holiday-dates";

const NAME_MAX_LENGTH = 200;

interface HolidayFormDialogProps {
	open: boolean;
	mode: "add" | "edit";
	scope: HolidayScope;
	initialData: Holiday | null;
	holidayTypes: HolidayType[];
	companyName: string;
	locationName?: string;
	isSubmitting: boolean;
	onClose: () => void;
	onSubmit: (values: HolidayFormValues) => void;
}

const HolidayFormDialog = ({
	open,
	mode,
	scope,
	initialData,
	holidayTypes,
	companyName,
	locationName,
	isSubmitting,
	onClose,
	onSubmit,
}: HolidayFormDialogProps) => {
	const { t } = useTranslation();
	const dayjsLocale = useDayjsLocale();
	const isEditMode = mode === "edit";

	const initialValues: HolidayFormValues = useMemo(
		() => ({
			name: initialData?.name ?? "",
			holidayTypeId: initialData?.holidayTypeId ?? "",
			date: initialData ? toDateKey(initialData.date) : "",
			isRecurringYearly: initialData?.isRecurringYearly ?? false,
		}),
		[initialData]
	);

	const validationSchema = useMemo(
		() =>
			Yup.object({
				name: Yup.string()
					.trim()
					.required(t("holidays_name-required"))
					.max(NAME_MAX_LENGTH, t("holidays_name-max-length")),
				holidayTypeId: Yup.number().required(t("holidays_type-required")),
				date: Yup.string()
					.required(t("holidays_date-required"))
					.test("valid-date", t("holidays_date-required"), (value) =>
						dayjs(value, "YYYY-MM-DD").isValid()
					),
				isRecurringYearly: Yup.boolean(),
			}),
		[t]
	);

	const handleSubmit = (
		values: HolidayFormValues,
		helpers: FormikHelpers<HolidayFormValues>
	) => {
		helpers.setSubmitting(false);
		onSubmit({ ...values, name: values.name.trim() });
	};

	// Company holidays apply everywhere; a location's own additions only there
	const scopeNote =
		scope === "location" && initialData?.locationId !== null
			? t("holidays_scope-note-location", { location: locationName })
			: t("holidays_scope-note-company", { company: companyName });

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			aria-labelledby="holiday-form-dialog-title"
		>
			<DialogTitle
				id="holiday-form-dialog-title"
				sx={{ padding: "32px 32px 20px 32px" }}
			>
				{isEditMode ? t("holidays_edit-title") : t("holidays_add-title")}
			</DialogTitle>
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ errors, touched, dirty, isValid, values, setFieldValue, setFieldTouched }) => (
					<Form>
						<DialogContent
							sx={{
								padding: "20px 32px",
								display: "flex",
								flexDirection: "column",
								gap: "20px",
							}}
						>
							<Field
								as={TextField}
								fullWidth
								name="name"
								label={t("holidays_field-name")}
								variant="filled"
								error={!!(errors.name && touched.name)}
								helperText={
									(touched.name && errors.name) || t("holidays_name-helper")
								}
								inputProps={{ maxLength: NAME_MAX_LENGTH }}
							/>
							<Box
								sx={{
									display: "flex",
									flexWrap: "wrap",
									justifyContent: "space-between",
									rowGap: "20px",
								}}
							>
								<FormControl
									variant="filled"
									sx={{ width: { xs: "100%", sm: "48%" } }}
									error={!!(errors.holidayTypeId && touched.holidayTypeId)}
								>
									<InputLabel id="holiday-type-label">
										{t("holidays_field-type")}
									</InputLabel>
									<Select
										labelId="holiday-type-label"
										name="holidayTypeId"
										value={values.holidayTypeId}
										onChange={(e) =>
											setFieldValue("holidayTypeId", Number(e.target.value))
										}
										onBlur={() => setFieldTouched("holidayTypeId", true)}
									>
										{holidayTypes.map((holidayType) => (
											<MenuItem key={holidayType.id} value={holidayType.id}>
												{holidayType.name}
											</MenuItem>
										))}
									</Select>
									{errors.holidayTypeId && touched.holidayTypeId ? (
										<FormHelperText>{errors.holidayTypeId}</FormHelperText>
									) : null}
								</FormControl>
								<Box sx={{ width: { xs: "100%", sm: "48%" } }}>
									<LocalizationProvider
										dateAdapter={AdapterDayjs}
										adapterLocale={dayjsLocale}
									>
										<DatePicker
											value={values.date ? dayjs(values.date) : null}
											// Formik's setFieldTouched validates the values from the
											// last render, so a touch in the same tick as the change
											// would re-validate the old (empty) date and leave a stale
											// "Date is required". Mark touched without validating and
											// let setFieldValue validate the new value.
											onChange={(value) => {
												setFieldTouched("date", true, false);
												if (value === null) {
													setFieldValue("date", "");
													return;
												}
												if (!dayjs(value).isValid()) return;
												setFieldValue("date", dayjs(value).format("YYYY-MM-DD"));
											}}
											format="MM/DD/YYYY"
											label={t("holidays_field-date")}
											sx={{ width: "100%" }}
											slotProps={{
												textField: {
													variant: "filled",
													name: "date",
													error: !!(errors.date && touched.date),
													helperText: touched.date ? errors.date : "",
													onBlur: () => setFieldTouched("date", true, false),
												},
											}}
										/>
									</LocalizationProvider>
								</Box>
							</Box>
							<Box>
								<FormControlLabel
									control={
										<Switch
											name="isRecurringYearly"
											checked={values.isRecurringYearly}
											onChange={(e) =>
												setFieldValue("isRecurringYearly", e.target.checked)
											}
										/>
									}
									label={t("holidays_field-repeats-yearly")}
								/>
								<Typography
									sx={{
										fontSize: "12px",
										lineHeight: "16px",
										color: "rgba(0, 0, 0, 0.6)",
										ml: "48px",
									}}
								>
									{t("holidays_repeats-yearly-caption")}
								</Typography>
							</Box>
							<Alert severity="info">{scopeNote}</Alert>
						</DialogContent>
						<DialogActions sx={{ padding: "25px" }}>
							<Button onClick={onClose}>{t("common_cancel")}</Button>
							<LoadingButton
								type="submit"
								variant="contained"
								color="primary"
								disabled={!isValid || !dirty || isSubmitting}
								loading={isSubmitting}
								sx={{ minWidth: "90px" }}
							>
								{isEditMode ? t("holidays_update") : t("common_add")}
							</LoadingButton>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default HolidayFormDialog;
